<template>
  <div
    v-if="topic"
    class="topic-component-container"
  >
    <div
      ref="top"
      class="top-content"
    >
      <div
        v-if="previousModalTopic"
        class="previous-modal"
        @click="back"
      >
        <icon
          name="arrow-left"
          class="previous-modal-icon"
        />&nbsp;
        <span
          class="previous-modal-text"
          v-html="`Back to ${previousModalTopic.title}`"
        />
      </div>
      <div
        v-if="showTitle"
        class="title"
        v-html="topic.title"
      />
      <div
        v-if="showTitle"
        class="bar"
      />
      <div
        class="content"
        v-html="topic.content"
      />
      <asset-associations-list
        :asset="topic"
        context="Content"
        class="associations"
        @flashcard-aa-clicked="handleFlashcardAssetAssociation"
      />
    </div>
    <div
      v-for="sub in children(topic)"
      :ref="`subtopic${sub.id}`"
      :key="sub.id"
      class="subtopics"
    >
      <accordion
        :expanded="expanded == sub"
        :topic="sub"
        @accordion-toggled="toggleAccordion"
      >
        <div
          class="subtopic-content"
          v-html="sub.content"
        />
        <asset-associations-list
          :asset="sub"
          context="Content"
          class="associations"
          @flashcard-aa-clicked="handleFlashcardAssetAssociation"
        />
        <div
          v-for="subSubtopic in children(sub)"
          :key="subSubtopic.id"
          class="subtopic-children"
        >
          <button
            class="subtopic-button"
            :tabindex="expanded == sub ? 0 : -1"
            @click="selectSubSubtopic(subSubtopic)"
          >
            <span v-html="subSubtopic.title" />
            <icon
              name="arrow"
              class="arrow"
              :variant="isMasteryApp ? '00dbfb' : '4fb9de'"
            />
          </button>
        </div>
      </accordion>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { asyncImport } from '@/utils';
import Accordion from '@components/topics/accordion';
const AssetAssociationsList = defineAsyncComponent(() => asyncImport(import('@components/assetAssociations/list')));
import Icon from '@components/singletons/icon';

import TopicsMixin from '@/mixins/topics';
import GlobalMixin from '@/mixins/global';

export default {
  name: 'Topic',
  components: { Accordion, AssetAssociationsList, Icon },
  mixins: [GlobalMixin, TopicsMixin],
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    // Typically the view is responsible for senidng topic analytics but in the case
    // of flashcard embedded topics, we need events to be sent for this component.
    // Use `sendAnalytics` with caution so that duplicate events arn't triggered.
    sendAnalytics: {
      type: Boolean,
      default: false,
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    // Source is used for analytics in the context of a topic embedded in a flashcard
    source: {
      type: Object,
      default: () => ({}),
    },
    subtopic: {
      type: Object,
    },
    topic: {
      type: Object,
    },
  },
  emits: ['topic-modal-back-clicked', 'nested-subtopic-selected', 'accordion-toggled'],
  data() {
    return {
      expanded: undefined,
    };
  },
  computed: {
    isMasteryApp() {
      return this.$store.getters['app/isMasteryApp'];
    },
    previousModalTopic() {
      if (this.modal && this.topic) {
        let path = this.topic.path.split('.');
        let id = path[path.length - 3];
        return this.$store.getters['topics/topics'][id];
      }
    },
  },
  mounted() {
    this.$watch('topic', (topic) => {
      if (topic) {
        this.$store.dispatch('topicStates/send', topic);

        if (this.sendAnalytics) {
          const params = {
            topic,
            source: this.source,
          };
          this.$analytics.events.viewedTopic(params);
        }
      }
    }, { immediate: true });

    // react to changes in subtopic, this needs to be here and not
    // in the watch block so we can guarantee refs are ready
    this.$watch('subtopic', (newSubtopic, previousSubtopic) => {
      if (this.topic) {
      // going forward or backward into an accordion topic within a modal
        if (this.children(this.topic).includes(newSubtopic)) {
          // accordion is already open so just skip
          if (this.expanded != newSubtopic) {
            this.openAccordion(newSubtopic, true);
            this.$nextTick(function() {
              this.scrollToAccordion(newSubtopic.id);
            });
          }
        // going forward or backward to the main topic modal, just close accordions
        } else {
          this.closeAccordions();
          if (this.topic == newSubtopic) {
            this.scrollToTop();
          }
        }
      }
    }, { immediate: true });
  },
  methods: {
    back() {
      let subtopic;

      // if in a modal, get the previous non-accordion topic
      if (this.previousModalTopic.path.split('.').length > 2) {
        subtopic = this.previousModalTopic;
      }

      this.$emit('topic-modal-back-clicked', subtopic);
    },
    closeAccordions() {
      this.expanded = null;
    },
    handleFlashcardAssetAssociation(id) {
      this.$router.push({
        name: 'assetAssociation#show',
        params: {
          assetAssociationId: id,
        },
        query: this.currentQuery,
      });
    },
    openAccordion(subtopic, viewed = false) {
      this.expanded = subtopic;
    },
    scrollToAccordion(topicId) {
      const ref = this.$refs[`subtopic${topicId}`][0];
      ref.scrollIntoView();
    },
    scrollToTop() {
      this.$refs.top.scrollIntoView();
    },
    selectSubSubtopic(subtopic) {
      this.$emit('nested-subtopic-selected', subtopic);
    },
    toggleAccordion(subtopic) {
      let close = this.expanded == subtopic;

      if (close) {
        this.closeAccordions();
      } else {
        this.openAccordion(subtopic);
        this.viewedAccordion(subtopic);
      }

      this.$emit('accordion-toggled', [close, subtopic, this.topic]);
    },
    viewedAccordion(subtopic) {
      this.$store.dispatch('topicStates/send', subtopic);
      if (this.sendAnalytics) {
        const path = subtopic.path.split('.');
        const parentId = path[path.length - 2]; // -1 for length, -1 to get the second to last id
        const parent = this.$store.getters['topics/topics'][parentId];
        const params = {
          topic: subtopic,
          parent,
          root: this.topic,
          source: this.source,
        };
        this.$analytics.events.viewedSubtopic(params);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.topic-component-container {
  .top-content {
    margin: auto;
    max-width: rem-calc(700);
    padding: rem-calc(48) rem-calc(16);
    position: relative;

    @include breakpoint(small) {
      padding: rem-calc(16) 0 rem-calc(16) rem-calc(16);
      margin-right: rem-calc(40);
    }
  }
  .previous-modal {
    position: absolute;
    top: rem-calc(10);
    left: rem-calc(10);
    cursor: pointer;

    img {
      position: relative;
      display: inline;
      top: 4px;
      width: 16px;
    }

    span {
      font-size: rem-calc(14);
      position: relative;
      left: 0;
      transition: all 120ms linear;
    }

    &:hover span {
      left: 3px;
      transition: all 120ms linear;
    }
  }

  .title {
    color: #1f2830;
    font-size: rem-calc(32);
    line-height: rem-calc(44);
    padding-bottom: rem-calc(16);
  }

  .bar {
    background: #00d0dc;
    height: 2px;
    margin-bottom: rem-calc(44);
    width: rem-calc(180);
  }

  .description {
    color: #516173;
    font-size: rem-calc(10);
    font-weight: $semi-bold;
    letter-spacing: 1px;
    margin-bottom: rem-calc(12);
    text-transform: uppercase;
  }

  .content {
    color: #354052;
    font-size: 1rem;
    line-height: 1.75rem;
    margin: 0 0 0 rem-calc(20);
  }

  .associations {
    padding: rem-calc(20) 0 0 rem-calc(20);
  }

  .subtopics {
    &:last-child .accordion-container:not(.open) :deep(.title) {
      border-bottom: 1px #e2e7ee solid;
    }

    .subtopic-content {
      font-size: rem-calc(16);
      line-height: 26px;
      color: #1a2b3b;
    }

    .subtopic-button {
      align-items: center;
      background-color: rgba(255, 255, 255, 0.55);
      box-shadow: 0px 0px 4px 0px rgba(0,0,0,0.15);
      border: none;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      margin: rem-calc(24) 0 0 0 !important;
      min-height: rem-calc(46);
      width: 100%;
      display: flex;
      justify-content: center;
      text-align: left;
      border-radius: 3px;
      font-weight: 600;
      cursor: pointer;
      color: #354052;
      padding-left: rem-calc(20);
      padding: rem-calc(8) rem-calc(12);
      border: 1px transparent solid;
      &:hover {
        border: 1px rgba(0,0,0,0.1) solid;
        transition: all 180ms linear;
      }

      span {
        margin-right: auto;
      }

      img {
        height: rem-calc(16);
        width: rem-calc(16);
      }
    }
  }
}
</style>
